import React from 'react';
import { AddBoxOutlined, DescriptionOutlined, Event, PermContactCalendar } from '@mui/icons-material';
import { ProviderMedicalRole } from '@uplift/types';
import { VideoCallDrawerContentContainer, VideoCallSidebar, VideoCallSidebarAlertProvider, VideoCallSidebarItem } from '../Layout';
import { ProfileDrawer } from './ProfileDrawer.component';
import { InCallScheduler } from './InCallScheduler';
import { useVideoContext } from '../../../hooks';
import { SidebarButtonType } from '@uplift/api';


export type ProviderSidebarProps = {
  appointmentId: string;
  patientId: string;
  medicalRole: ProviderMedicalRole | null;
  sessionId: string;
  renderPsychiatryDrawer?: VideoCallSidebarItem['renderContent'];
  SessionNoteComponent?: React.ComponentType<{ sessionId: string; patientId: string }>;
};


export const ProviderSidebar: React.VFC<ProviderSidebarProps> = ({ SessionNoteComponent, ...props }) => {
  const { isSharingScreen } = useVideoContext();
  const sidebarItems: VideoCallSidebarItem[] = React.useMemo(() => {
    const sidebarItems: VideoCallSidebarItem[] = [];

    if (props.medicalRole === ProviderMedicalRole.psychiatrist && SessionNoteComponent) {
      sidebarItems.push({
        icon: <DescriptionOutlined />,
        renderContent: (onClose) => {
          return (
            <VideoCallDrawerContentContainer onClose={onClose} title="Session Notes">
              <SessionNoteComponent
                sessionId={props.sessionId}
                patientId={props.patientId}
              />
            </VideoCallDrawerContentContainer>
          );
        },
        tooltipTitle: SidebarButtonType.notes,
        id: 'notes',
        keepMounted: true,
        contentWidth: 554,
      });
    }

    sidebarItems.push({
      icon: <PermContactCalendar />,
      renderContent: (onClose) => <ProfileDrawer onClose={onClose} patientId={props.patientId} medicalRole={props.medicalRole} />,
      tooltipTitle: SidebarButtonType.profile,
      id: 'profile',
      keepMounted: true,
      contentWidth: 440
    });

    if (props.medicalRole === ProviderMedicalRole.therapist && props.renderPsychiatryDrawer) {
      sidebarItems.push({
        icon: <AddBoxOutlined />,
        renderContent: props.renderPsychiatryDrawer,
        // TODO: this will be false once we can fix the rendering issue with gqty not re-rendering based on new data
        keepMounted: false,
        tooltipTitle: SidebarButtonType.refer,
        id: 'refer',
        contentWidth: 440
      });
    }

    sidebarItems.push({
      icon: <Event />,
      renderContent: (onClose) => {
        return (
          <VideoCallSidebarAlertProvider contentWidth="440px">
            <InCallScheduler
              patientId={props.patientId}
              appointmentId={props.appointmentId}
              onClose={onClose}
            />
          </VideoCallSidebarAlertProvider>
        );
      },
      tooltipTitle: SidebarButtonType.scheduling,
      id: 'scheduling',
      contentWidth: 440,
      disabled: isSharingScreen,
    });

    return sidebarItems;
  }, [ SessionNoteComponent, props.medicalRole, props.patientId, props.renderPsychiatryDrawer, props.sessionId, props.appointmentId, isSharingScreen ]);


  return <VideoCallSidebar sidebarItems={sidebarItems} />;
};
